import type { AxiosInstance } from 'axios';
import type { ServiceResultT } from '../types/common/ServiceResultT';
import type { ServiceResult } from '../types/common/ServiceResult';
import type {
  LoginRequest,
  LoginResponse,
  CurrentUserViewModel,
  RefreshTokenRequest,
  RegisterRequest,
  RegisterResponse,
  ActivateEmailRequest,
  CorporateUserRegisterRequest,
  ForgotPasswordRequest,
  ChangeForgottenPasswordRequest,
  ChangePasswordRequest,
} from '../types/auth';
import { apiConfig } from '../client/config';
import { getApiClient } from '../client/apiClientRef';

/**
 * Auth service – does not inherit BaseService.
 * Login/register/activate return ServiceResult; app stores token/user on success.
 * Logout: app clears storage and resets state (client does not clear storage).
 * Uses getApiClient() to avoid circular dependency with client → interceptors → AuthService → client.
 */
export class AuthService {
  private static readonly BASE = '/api/auth';

  private get client(): AxiosInstance {
    const c = getApiClient();
    if (!c) throw new Error('API client not initialized');
    return c;
  }

  async login(payload: LoginRequest): Promise<ServiceResultT<LoginResponse>> {
    const { data } = await this.client.post<ServiceResultT<LoginResponse>>(
      `${AuthService.BASE}/login`,
      payload
    );
    return data;
  }

  async register(payload: RegisterRequest): Promise<ServiceResultT<RegisterResponse>> {
    const { data } = await this.client.post<ServiceResultT<RegisterResponse>>(
      `${AuthService.BASE}/register`,
      payload
    );
    return data;
  }

  async registerCorporate(
    payload: CorporateUserRegisterRequest
  ): Promise<ServiceResultT<RegisterResponse>> {
    const { data } = await this.client.post<ServiceResultT<RegisterResponse>>(
      `${AuthService.BASE}/register/corporate-user`,
      payload
    );
    return data;
  }

  async activateEmail(payload: ActivateEmailRequest): Promise<ServiceResultT<string | null>> {
    const { data } = await this.client.post<ServiceResultT<string | null>>(
      `${AuthService.BASE}/activate-email`,
      payload
    );
    return data;
  }

  async forgotPassword(payload: ForgotPasswordRequest): Promise<ServiceResult> {
    const { data } = await this.client.post<ServiceResult>(
      `${AuthService.BASE}/forgot-password`,
      payload
    );
    return data;
  }

  async changeForgottenPassword(
    payload: ChangeForgottenPasswordRequest
  ): Promise<ServiceResult> {
    const { data } = await this.client.post<ServiceResult>(
      `${AuthService.BASE}/change-forgotten-password`,
      payload
    );
    return data;
  }

  /** Change password for authenticated user (POST /api/auth/change-password). */
  async changePassword(payload: ChangePasswordRequest): Promise<ServiceResult> {
    const { data } = await this.client.post<ServiceResult>(
      `${AuthService.BASE}/change-password`,
      payload
    );
    return data;
  }

  async refreshToken(payload: RefreshTokenRequest): Promise<ServiceResultT<LoginResponse>> {
    const { data } = await this.client.post<ServiceResultT<LoginResponse>>(
      `${AuthService.BASE}/refresh-token`,
      payload
    );
    return data;
  }

  async logout(): Promise<ServiceResult> {
    const { data } = await this.client.post<ServiceResult>(`${AuthService.BASE}/logout`);
    return data;
  }

  /** Current user with roles (GET /api/auth/me). Use when authenticated to hydrate user/roles. */
  async getMe(): Promise<ServiceResultT<CurrentUserViewModel | null>> {
    const { data } = await this.client.get<ServiceResultT<CurrentUserViewModel | null>>(
      `${AuthService.BASE}/me`
    );
    return data;
  }

  /** URL for redirecting user to Google login (GET /api/auth/google-login). */
  getGoogleLoginUrl(): string {
    const base = apiConfig.baseURL.replace(/\/$/, '');
    return `${base}${AuthService.BASE}/google-login`;
  }

  /** URL for Google OAuth callback (GET /api/auth/google-callback). */
  getGoogleCallbackUrl(): string {
    const base = apiConfig.baseURL.replace(/\/$/, '');
    return `${base}${AuthService.BASE}/google-callback`;
  }
}

export const authService = new AuthService();
